/**
 * Dashboard API (New Backend)
 * Implements `DASHBOARD_DOCUMENTATION.md` using GraphQL read queries.
 *
 * Data flow (per doc §3): Organization context → decode org ID → parallel queries
 * (orderStatistics, organizationProviders, monthly revenue from payouts/invoices,
 * allOrders, providerServices) → transform → render
 * (KPICards, ActionPanel, OrderOverview table, ServicesChart).
 *
 * - Upcoming services table: allOrders (same query shape as OrderDetails); filter by
 *   service status (PENDING/CREATED/ASSIGNED/SCHEDULED/IN_PROGRESS/ACCEPTED) so scheduled
 *   services appear regardless of order status.
 * - Services chart: provider services filtered by status done/completed and
 *   current week, grouped by day (Mon–Sat).
 *
 * No fallback/mock data. Accept/decline actions are REST and handled elsewhere.
 */

import { graphqlQuery } from '@/services/graphql'
import { decodeRelayId } from '@/utils/id'
import { financialsApi } from '@/api/financials'
import type {
  AssignableProvider,
  BreakdownDatum,
  DashboardStats,
  OrderStatistics,
  PossibleProvider,
  ProviderOrder,
  ProviderService,
  SalesInvoice,
  UpcomingServiceRow,
  WeeklyPerformance,
  WeeklyServiceData,
} from '@/types/dashboard'
import { endOfWeek, isWithinInterval, startOfWeek, subWeeks } from 'date-fns'

// ==================== GraphQL Queries (per documentation) ====================

const GET_ORDER_STATISTICS = `
  query GetOrderStatistics($companyId: ID, $startDate: DateTime, $endDate: DateTime) {
    orderStatistics(companyId: $companyId, startDate: $startDate, endDate: $endDate) {
      totalOrders
      draftOrders
      activeOrders
      completedOrders
      cancelledOrders
      totalRevenue
      averageOrderValue
      ordersByStatus {
        status
        count
        totalRevenue
      }
    }
  }
`

// Providers (for Active count) - match ProvidersPage logic, not UI
const GET_ORGANIZATION_PROVIDERS = `
  query GetOrganizationProviders($organizationId: ID!) {
    organizationProviders(organizationId: $organizationId) {
      id
      status
      statusDisplay
    }
  }
`

const GET_SALES_INVOICES = `
  query GetSalesInvoices($orderId: ID, $paymentStatus: String) {
    salesInvoices(orderId: $orderId, paymentStatus: $paymentStatus) {
      id
      status
      totalAmount
      currency
      createdAt
      isPaid
      isOverdue
    }
  }
`

const GET_PROVIDER_SERVICES = `
query GetProviderServices($status: String, $pendingAcceptance: Boolean, $limit: Int) {
  providerServices(
    status: $status
    pendingAcceptance: $pendingAcceptance
    limit: $limit
  ) {
    id
    status
    canAccept
    canDecline
    canStart
    canMarkDone
    acceptanceDeadline
    hasPendingSurvey
    locationAddress
    locationGoogleMapsLink
    serviceTemplate {
      nameEn
      nameAr
    }
    appointment {
      scheduledStart
      scheduledEnd
      services {
        id
        possibleProviders {
          id
          providerId
          provider {
            id
            fullName
            phoneNumber
          }
        }
      }
    }
    order {
      id
      orderNumber
      status
    }
  }
}
`

/**
 * Entity assignment queue — the unassigned services an Entity admin may dispatch on the
 * External Dashboard (status pending/searching/assign_manually). Each row carries the
 * entity-scoped `assignableProviders` list so the picker no longer needs the
 * possibleProviders ∩ organizationProviders intersection. Source of truth for Action Required.
 */
const GET_ENTITY_ASSIGNMENT_QUEUE = `
query EntityAssignmentQueue($status: String, $limit: Int) {
  entityAssignmentQueue(status: $status, limit: $limit) {
    id
    status
    canEntityAssign
    assignableProviders {
      id
      fullName
      phoneNumber
    }
    serviceTemplate {
      nameEn
      nameAr
    }
    appointment {
      scheduledStart
      scheduledEnd
    }
    order {
      id
      orderNumber
      status
    }
    acceptanceDeadline
  }
}
`

const GET_PROVIDER_ORDERS = `
  query GetProviderOrders($status: String, $organizationId: ID, $limit: Int) {
    providerOrders(status: $status, organizationId: $organizationId, limit: $limit) {
      id
      orderNumber
      status
      createdAt

      services {
        id
        status
        providerName
        providerPhone
        locationAddress
        locationGoogleMapsLink
        acceptanceDeadline
        canAccept
        canDecline

        serviceTemplate {
          nameEn
          nameAr
        }

        appointment {
          scheduledStart
          scheduledEnd
        }
      }
    }
  }
`

/**
 * Fetch orders scoped to the logged-in company with nested services for dashboard.
 * Uses companyOrders so only orders belonging to the current organization are returned.
 * No order-status filter: we filter by service status client-side so scheduled services
 * appear regardless of order status.
 */
const GET_COMPANY_ORDERS_FOR_DASHBOARD = `
  query CompanyOrdersForDashboard(
    $companyId: ID!
    $limit: Int
  ) {
    companyOrders(
      companyId: $companyId
      limit: $limit
    ) {
      id
      orderNumber
      status
      completedAt
      updatedAt
      services {
        id
        status
        serviceName
        providerName
        providerPhone
        appointmentScheduledStart
        appointmentScheduledEnd
        locationAddress
        locationGoogleMapsLink
        completedAt
      }
    }
  }
`

// ==================== Response Types ====================

interface OrderStatisticsResponse {
  orderStatistics: OrderStatistics | null
}

interface OrganizationProvidersResponse {
  organizationProviders: Array<{
    id: string
    status?: string | null
    statusDisplay?: string | null
  }> | null
}

interface SalesInvoicesResponse {
  salesInvoices: SalesInvoice[] | null
}

interface ProviderServicesResponse {
  providerServices: ProviderServiceRaw[] | null
}

interface ProviderServiceRaw {
  id: string
  status: string
  canAccept: boolean
  canDecline: boolean
  canStart?: boolean
  canMarkDone?: boolean
  acceptanceDeadline?: string | null
  hasPendingSurvey?: boolean
  locationAddress?: string | null
  locationGoogleMapsLink?: string | null
  serviceTemplate?: ProviderService['serviceTemplate']
  appointment?: {
    scheduledStart?: string | null
    scheduledEnd?: string | null
    services?: Array<{
      id?: string | null
      possibleProviders?: PossibleProvider[] | null
    }> | null
  } | null
  order?: ProviderService['order']
}

interface ProviderOrdersResponse {
  providerOrders: ProviderOrder[] | null
}

interface EntityAssignmentQueueRaw {
  id: string
  status: string
  canEntityAssign?: boolean | null
  assignableProviders?: AssignableProvider[] | null
  serviceTemplate?: ProviderService['serviceTemplate']
  appointment?: {
    scheduledStart?: string | null
    scheduledEnd?: string | null
  } | null
  order?: {
    id: string
    orderNumber?: string | null
    status?: string | null
  } | null
  acceptanceDeadline?: string | null
}

interface EntityAssignmentQueueResponse {
  entityAssignmentQueue: EntityAssignmentQueueRaw[] | null
}

/** Order node shape from companyOrders (with nested services for dashboard) */
interface UpcomingOrderNode {
  id: string
  orderNumber: string
  status: string
  completedAt?: string | null
  updatedAt?: string | null
  services: Array<{
    id: string
    status: string
    serviceName: string
    appointmentScheduledStart: string | null
    appointmentScheduledEnd: string | null
    providerName: string | null
    locationAddress: string | null
    locationGoogleMapsLink: string | null
    completedAt?: string | null
    /**
     * Category/type of the service (e.g. nursing, physio). Optional until the backend
     * exposes it on the companyOrders `services` node; once added to
     * GET_COMPANY_ORDERS_FOR_DASHBOARD the "by category" breakdown populates automatically.
     */
    serviceCategory?: string | null
  }>
}

interface CompanyOrdersForDashboardResponse {
  companyOrders: UpcomingOrderNode[] | null
}

// ==================== Helpers ====================

function orgUuid(organizationId: string): string {
  // Docs say backend expects raw UUID; this helper keeps us compatible if Relay IDs appear.
  return decodeRelayId(organizationId) ?? organizationId
}

function parseAmount(amount: string | null | undefined): number {
  if (!amount) return 0
  const n = Number.parseFloat(amount)
  return Number.isFinite(n) ? n : 0
}

function calculateCurrentMonthRevenue(invoices: SalesInvoice[]): { amount: number; currency: string } {
  const now = new Date()
  const month = now.getMonth()
  const year = now.getFullYear()

  let currency = invoices[0]?.currency || 'SAR'
  let amount = 0

  for (const inv of invoices) {
    if (!inv?.createdAt) continue
    const d = new Date(inv.createdAt)
    const isCurrentMonth = d.getMonth() === month && d.getFullYear() === year
    const isPaid = inv.status === 'paid' || inv.isPaid === true
    if (!isCurrentMonth || !isPaid) continue

    if (inv.currency) currency = inv.currency
    amount += parseAmount(inv.totalAmount)
  }

  return { amount, currency }
}

/**
 * Monthly revenue = sum of grossAmount from ALL paid payout statements.
 * Every paid statement counts regardless of period dates.
 */
function calculateMonthlyRevenueFromPayouts(
  statements: Array<{
    status?: string
    grossAmount?: number | null
    totalProviderCut?: number
    totalProviderVat?: number
  }>
): { amount: number; currency: string } {
  let total = 0

  for (const stmt of statements) {
    if ((stmt.status || '').toLowerCase() !== 'paid') continue

    const amount =
      typeof stmt.grossAmount === 'number' && Number.isFinite(stmt.grossAmount)
        ? stmt.grossAmount
        : (stmt.totalProviderCut || 0) + (stmt.totalProviderVat || 0)

    total += amount
  }

  return { amount: total, currency: 'SAR' }
}

function mapServiceStatus(status: string): UpcomingServiceRow['status'] {
  const key = (status || '').toLowerCase()
  if (key === 'assigned') return 'PENDING'
  if (key === 'accepted' || key === 'scheduled') return 'SCHEDULED'
  if (key === 'in_progress') return 'IN_PROGRESS'
  if (key === 'done' || key === 'completed') return 'COMPLETED'
  if (key === 'cancelled') return 'CANCELLED'
  return 'PENDING'
}

/** Reflect backend service status as-is on dashboard (no remapping) */
function mapOrderDetailsServiceStatus(status: string): string {
  return (status || '').toUpperCase().trim() || '—'
}

/**
 * Build Action Required list from same allOrders data (so ASSIGNED services appear).
 * providerServices(status: 'ASSIGNED') may not return the same set; allOrders does.
 */
function extractPossibleProviders(service: ProviderServiceRaw): PossibleProvider[] {
  const appointmentServices = service.appointment?.services ?? []
  const matching = appointmentServices.find((s) => s.id === service.id)
  if (matching?.possibleProviders?.length) {
    return matching.possibleProviders
  }

  const flattened: PossibleProvider[] = []
  for (const s of appointmentServices) {
    for (const p of s.possibleProviders ?? []) {
      flattened.push(p)
    }
  }
  return flattened
}

function mapProviderServiceRaw(raw: ProviderServiceRaw): ProviderService {
  const { appointment, ...rest } = raw
  return {
    ...rest,
    appointment: appointment
      ? {
          scheduledStart: appointment.scheduledStart,
          scheduledEnd: appointment.scheduledEnd,
        }
      : null,
    possibleProviders: extractPossibleProviders(raw),
  }
}

/** Map an entityAssignmentQueue row to the ProviderService shape the ActionPanel renders. */
function mapEntityQueueRaw(raw: EntityAssignmentQueueRaw): ProviderService {
  return {
    id: raw.id,
    status: raw.status,
    canAccept: true,
    canDecline: true,
    acceptanceDeadline: raw.acceptanceDeadline ?? null,
    serviceTemplate: raw.serviceTemplate ?? null,
    appointment: raw.appointment
      ? {
          scheduledStart: raw.appointment.scheduledStart,
          scheduledEnd: raw.appointment.scheduledEnd,
        }
      : null,
    order: raw.order
      ? {
          id: decodeRelayId(raw.order.id) ?? raw.order.id,
          orderNumber: raw.order.orderNumber,
          status: raw.order.status,
        }
      : null,
    assignableProviders: raw.assignableProviders ?? [],
    canEntityAssign: raw.canEntityAssign ?? undefined,
  }
}

function buildWeeklyCompletedServicesChart(services: ProviderService[]): WeeklyServiceData[] {
  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const
  const dayNamesFull = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ] as const

  const now = new Date()
  const weekStart = startOfWeek(now, { weekStartsOn: 1 })
  const weekEnd = endOfWeek(now, { weekStartsOn: 1 })

  const counts: Record<string, number> = {}
  for (const d of dayNames) counts[d] = 0

  for (const s of services) {
    const status = (s.status || '').toLowerCase()
    if (!['done', 'completed'].includes(status)) continue
    const start = s.appointment?.scheduledStart
    if (!start) continue

    const date = new Date(start)
    if (!isWithinInterval(date, { start: weekStart, end: weekEnd })) continue

    const dayIndexMon0 = (date.getDay() + 6) % 7 // Mon=0 ... Sun=6
    const day = dayNames[dayIndexMon0]
    counts[day] += 1
  }

  const total = Object.values(counts).reduce((a, b) => a + b, 0)
  return dayNames.map((day, idx) => ({
    day,
    dayFull: dayNamesFull[idx],
    services: counts[day],
    percentage: total > 0 ? Math.round((counts[day] / total) * 100) : 0,
  }))
}

// ==================== Weekly performance aggregation ====================

/** An inclusive [weekStart, weekEnd] range used to scope service aggregations. */
interface ServiceWindow {
  weekStart: Date
  weekEnd: Date
}

type OrderServiceNode = UpcomingOrderNode['services'][number]

/** Max rows returned by the top-N breakdown charts. */
const TOP_N = 5

/** The current ISO week (Mon–Sun). */
function currentWeekWindow(): ServiceWindow {
  const now = new Date()
  return {
    weekStart: startOfWeek(now, { weekStartsOn: 1 }),
    weekEnd: endOfWeek(now, { weekStartsOn: 1 }),
  }
}

/** The ISO week `offset` weeks before the current one (offset 1 = last week). */
function weekWindowBefore(offset: number): ServiceWindow {
  const ref = subWeeks(new Date(), offset)
  return {
    weekStart: startOfWeek(ref, { weekStartsOn: 1 }),
    weekEnd: endOfWeek(ref, { weekStartsOn: 1 }),
  }
}

/**
 * Resolve the date a service should be bucketed under, regardless of status.
 * completedAt → appointmentScheduledStart → order.completedAt → order.updatedAt.
 */
function resolveServiceDate(order: UpcomingOrderNode, service: OrderServiceNode): Date | null {
  const dateStr =
    service.completedAt
    ?? service.appointmentScheduledStart
    ?? order.completedAt
    ?? order.updatedAt
    ?? null
  if (!dateStr) return null
  const date = new Date(dateStr)
  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * A service counts as completed if its own status is done/completed OR the parent
 * order is completed (the backend may not propagate status down to each service).
 */
function isCompletedService(order: UpcomingOrderNode, service: OrderServiceNode): boolean {
  const orderCompleted = (order.status || '').toLowerCase() === 'completed'
  const serviceStatus = (service.status || '').toLowerCase()
  return ['done', 'completed'].includes(serviceStatus) || orderCompleted
}

/** True when the service's resolved date falls inside the window. */
function isServiceInWindow(
  order: UpcomingOrderNode,
  service: OrderServiceNode,
  window: ServiceWindow,
): boolean {
  const date = resolveServiceDate(order, service)
  if (!date) return false
  return isWithinInterval(date, { start: window.weekStart, end: window.weekEnd })
}

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const
const DAY_NAMES_FULL = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const

/**
 * Build weekly completed services chart from allOrders nodes (order + services).
 * Completed services are bucketed by weekday (Mon–Sun) within the given window
 * (defaults to the current ISO week, so the existing caller is unchanged).
 */
function buildWeeklyCompletedServicesChartFromOrderNodes(
  orders: UpcomingOrderNode[],
  window: ServiceWindow = currentWeekWindow(),
): WeeklyServiceData[] {
  const counts: Record<string, number> = {}
  for (const d of DAY_NAMES) counts[d] = 0

  for (const order of orders) {
    for (const s of order.services || []) {
      if (!isCompletedService(order, s)) continue
      if (!isServiceInWindow(order, s, window)) continue

      const date = resolveServiceDate(order, s)!
      const dayIndexMon0 = (date.getDay() + 6) % 7
      counts[DAY_NAMES[dayIndexMon0]] += 1
    }
  }

  const total = Object.values(counts).reduce((a, b) => a + b, 0)
  return DAY_NAMES.map((day, idx) => ({
    day,
    dayFull: DAY_NAMES_FULL[idx],
    services: counts[day],
    percentage: total > 0 ? Math.round((counts[day] / total) * 100) : 0,
  }))
}

/** Total completed services within the window. */
function countCompletedInWindow(orders: UpcomingOrderNode[], window: ServiceWindow): number {
  let total = 0
  for (const order of orders) {
    for (const s of order.services || []) {
      if (isCompletedService(order, s) && isServiceInWindow(order, s, window)) total += 1
    }
  }
  return total
}

/** Total services of any status whose resolved date falls within the window. */
function countServicesInWindow(orders: UpcomingOrderNode[], window: ServiceWindow): number {
  let total = 0
  for (const order of orders) {
    for (const s of order.services || []) {
      if (isServiceInWindow(order, s, window)) total += 1
    }
  }
  return total
}

/** Completion rate (0..100) = completed / all services in the window. */
function completionRateInWindow(orders: UpcomingOrderNode[], window: ServiceWindow): number {
  const total = countServicesInWindow(orders, window)
  if (total === 0) return 0
  return Math.round((countCompletedInWindow(orders, window) / total) * 100)
}

/** Group all in-window services by mapped status (pending/scheduled/…/completed). */
function buildStatusBreakdown(
  orders: UpcomingOrderNode[],
  window: ServiceWindow,
): BreakdownDatum[] {
  const counts = new Map<string, number>()
  for (const order of orders) {
    for (const s of order.services || []) {
      if (!isServiceInWindow(order, s, window)) continue
      const status = mapServiceStatus(s.status)
      counts.set(status, (counts.get(status) || 0) + 1)
    }
  }
  // Fixed pipeline order so the donut colours stay stable.
  const statusOrder: UpcomingServiceRow['status'][] = [
    'PENDING',
    'SCHEDULED',
    'IN_PROGRESS',
    'COMPLETED',
    'CANCELLED',
  ]
  return statusOrder
    .filter((s) => counts.has(s))
    .map((s) => ({ name: s, count: counts.get(s)! }))
}

/** Top-N completed services in the window, grouped by a key (serviceName / serviceCategory). */
function buildCompletedBreakdown(
  orders: UpcomingOrderNode[],
  window: ServiceWindow,
  keyOf: (service: OrderServiceNode) => string | null | undefined,
): BreakdownDatum[] {
  const counts = new Map<string, number>()
  for (const order of orders) {
    for (const s of order.services || []) {
      if (!isCompletedService(order, s)) continue
      if (!isServiceInWindow(order, s, window)) continue
      const key = keyOf(s)
      if (!key) continue
      counts.set(key, (counts.get(key) || 0) + 1)
    }
  }
  return [...counts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, TOP_N)
}

function buildUpcomingServiceRows(orders: ProviderOrder[]): UpcomingServiceRow[] {
  // Flatten services out of orders and sort by appointment time
  const rows: Array<{ scheduledStart?: string | null; row: UpcomingServiceRow }> = []

  for (const order of orders) {
    for (const service of order.services || []) {
      const statusKey = (service.status || '').toLowerCase()
      const isUpcoming = statusKey === 'scheduled'
      if (!isUpcoming) continue

      const scheduledStart = service.appointment?.scheduledStart || null
      const time = scheduledStart ? new Date(scheduledStart).toLocaleString() : '—'

      rows.push({
        scheduledStart,
        row: {
          id: order.id,
          customId: order.orderNumber,
          time,
          service: service.serviceTemplate?.nameEn || service.serviceTemplate?.nameAr || 'Service',
          pickupLocation: service.locationAddress || undefined,
          pickupLocationUrl: service.locationGoogleMapsLink || undefined,
          provider: service.providerName || '—',
          status: mapServiceStatus(service.status),
        },
      })
    }
  }

  rows.sort((a, b) => {
    const da = a.scheduledStart ? new Date(a.scheduledStart).getTime() : Number.POSITIVE_INFINITY
    const db = b.scheduledStart ? new Date(b.scheduledStart).getTime() : Number.POSITIVE_INFINITY
    return da - db
  })

  return rows.map((x) => x.row)
}

/**
 * Build the "pending acceptance" Action Required list from company order nodes:
 * services on a `confirmed` order whose provider is assigned but has not accepted yet.
 * These render the Accept/Reject variant (accept/reject on behalf of the assigned provider)
 * rather than the pick-a-provider assign flow. Reuses already-fetched company order data.
 */
function buildPendingAcceptanceServicesFromOrderNodes(
  orders: UpcomingOrderNode[],
): ProviderService[] {
  const services: ProviderService[] = []

  for (const order of orders) {
    if ((order.status || '').toLowerCase() !== 'confirmed') continue

    for (const service of order.services || []) {
      if ((service.status || '').toLowerCase() !== 'assigned') continue

      services.push({
        id: service.id,
        status: service.status,
        canAccept: true,
        canDecline: true,
        acceptanceDeadline: null,
        serviceTemplate: { nameEn: service.serviceName, nameAr: null },
        appointment: {
          scheduledStart: service.appointmentScheduledStart,
          scheduledEnd: service.appointmentScheduledEnd,
        },
        order: {
          id: decodeRelayId(order.id) ?? order.id,
          orderNumber: order.orderNumber,
          status: order.status,
        },
        providerName: service.providerName ?? null,
        requiresAcceptance: true,
      })
    }
  }

  return services
}

/** Service statuses that appear in Upcoming table: only SCHEDULED */
const UPCOMING_SERVICE_STATUSES = ['SCHEDULED'] as const

function isUpcomingServiceStatus(status: string): boolean {
  const key = (status || '').toUpperCase().trim()
  return UPCOMING_SERVICE_STATUSES.some((s) => s === key)
}

/**
 * Build upcoming service rows from order-details-style nodes (allOrders with services).
 * Same data shape as OrderDetails. Filter by service status only so scheduled services
 * appear regardless of order status.
 */
function buildUpcomingServiceRowsFromOrderNodes(orders: UpcomingOrderNode[]): UpcomingServiceRow[] {
  const rows: Array<{ scheduledStart: string | null; row: UpcomingServiceRow }> = []

  for (const order of orders) {
    for (const service of order.services || []) {
      if (!isUpcomingServiceStatus(service.status)) continue

      const scheduledStart = service.appointmentScheduledStart || null
      const time = scheduledStart ? new Date(scheduledStart).toLocaleString() : '—'

      const orderIdForNav = decodeRelayId(order.id) ?? order.id
      const row: UpcomingServiceRow = {
        id: orderIdForNav,
        serviceId: service.id,
        customId: order.orderNumber,
        time,
        service: service.serviceName || 'Service',
        pickupLocation: service.locationAddress || undefined,
        pickupLocationUrl: service.locationGoogleMapsLink || undefined,
        provider: service.providerName || '—',
        status: mapOrderDetailsServiceStatus(service.status),
      }
      rows.push({ scheduledStart, row })
    }
  }

  rows.sort((a, b) => {
    const da = a.scheduledStart ? new Date(a.scheduledStart).getTime() : Number.POSITIVE_INFINITY
    const db = b.scheduledStart ? new Date(b.scheduledStart).getTime() : Number.POSITIVE_INFINITY
    return da - db
  })

  return rows.map((x) => x.row)
}

// ==================== Fetchers ====================

export async function fetchOrderStatistics(
  companyId?: string,
  startDate?: Date,
  endDate?: Date,
): Promise<OrderStatistics | null> {
  const resp = await graphqlQuery<OrderStatisticsResponse>(GET_ORDER_STATISTICS, {
    companyId: companyId ?? null,
    startDate: startDate?.toISOString() ?? null,
    endDate: endDate?.toISOString() ?? null,
  })
  return resp.data?.orderStatistics ?? null
}

/**
 * Active Providers count
 * Must match ProvidersPage logic:
 * `statusKey.includes('active') && !statusKey.includes('inactive')`
 */
export async function fetchActiveProvidersCount(organizationId: string): Promise<number> {
  const resp = await graphqlQuery<OrganizationProvidersResponse>(GET_ORGANIZATION_PROVIDERS, {
    organizationId: orgUuid(organizationId),
  })
  const providers = resp.data?.organizationProviders ?? []
  return providers.filter((p) => {
    const statusKey = (p.status || p.statusDisplay || '').toLowerCase()
    return statusKey.includes('active') && !statusKey.includes('inactive')
  }).length
}

export async function fetchPaidSalesInvoices(): Promise<SalesInvoice[]> {
  const resp = await graphqlQuery<SalesInvoicesResponse>(GET_SALES_INVOICES, {
    paymentStatus: 'paid',
  })
  return resp.data?.salesInvoices ?? []
}

/**
 * Monthly revenue from paid payout statements only.
 * Only statements with status "paid" are fetched and summed.
 */
async function fetchMonthlyRevenue(): Promise<{ amount: number; currency: string }> {
  // Walk every page so the total is not capped at the backend's page size.
  const statements = await financialsApi.listAllPayoutStatements({ status: 'paid' })
  return calculateMonthlyRevenueFromPayouts(statements)
}

export async function fetchProviderServices(options?: {
  status?: string
  pendingAcceptance?: boolean
  limit?: number
}): Promise<ProviderService[]> {
  const resp = await graphqlQuery<ProviderServicesResponse>(GET_PROVIDER_SERVICES, {
    status: options?.status,
    pendingAcceptance: options?.pendingAcceptance,
    limit: options?.limit ?? 100,
  })
  return (resp.data?.providerServices ?? []).map(mapProviderServiceRaw)
}

/**
 * Fetch the Entity assignment queue (unassigned services the entity may dispatch).
 * Returns [] for non-entity sessions (backend scopes by ProfileEntity).
 */
export async function fetchEntityAssignmentQueue(options?: {
  status?: string
  limit?: number
}): Promise<ProviderService[]> {
  const resp = await graphqlQuery<EntityAssignmentQueueResponse>(GET_ENTITY_ASSIGNMENT_QUEUE, {
    status: options?.status,
    limit: options?.limit ?? 100,
  })
  return (resp.data?.entityAssignmentQueue ?? []).map(mapEntityQueueRaw)
}

export async function fetchProviderOrders(options: {
  status?: string
  organizationId?: string
  limit?: number
}): Promise<ProviderOrder[]> {
  const resp = await graphqlQuery<ProviderOrdersResponse>(GET_PROVIDER_ORDERS, {
    status: options.status,
    organizationId: options.organizationId ? orgUuid(options.organizationId) : undefined,
    limit: options.limit ?? 50,
  })
  return resp.data?.providerOrders ?? []
}

// ==================== Company orders for dashboard ====================

/**
 * Fetch orders scoped to the logged-in company with nested services.
 * No order-status filter: service status filtering is done client-side so
 * scheduled services appear regardless of order status.
 */
async function fetchUpcomingOrdersOrderDetailsStyle(
  companyId: string,
  limit = 150,
): Promise<UpcomingOrderNode[]> {
  const resp = await graphqlQuery<CompanyOrdersForDashboardResponse>(
    GET_COMPANY_ORDERS_FOR_DASHBOARD,
    { companyId, limit },
  )
  const orders = (resp.data?.companyOrders ?? []).filter((n) => n?.id)

  orders.sort((a, b) => {
    const aTime = firstScheduledTime(a)
    const bTime = firstScheduledTime(b)
    return aTime - bTime
  })
  return orders
}

function firstScheduledTime(order: UpcomingOrderNode): number {
  let min = Number.POSITIVE_INFINITY
  for (const s of order.services || []) {
    const start = s.appointmentScheduledStart
    if (start) min = Math.min(min, new Date(start).getTime())
  }
  return min
}

// ==================== Main Aggregation ====================

export async function fetchDashboardData(organizationId: string): Promise<DashboardStats> {
  const orgId = orgUuid(organizationId)

  const [
    orderStatistics,
    activeProvidersCount,
    monthlyRevenueResult,
    upcomingOrderNodes,
    entityAssignmentQueue,
  ] = await Promise.all([
    fetchOrderStatistics(orgId),
    fetchActiveProvidersCount(organizationId),
    // KPI 3 Monthly Revenue: from payout data (doc 14.5), fallback to paid sales invoices
    fetchMonthlyRevenue(),
    // companyOrders data for Upcoming (SCHEDULED)
    fetchUpcomingOrdersOrderDetailsStyle(orgId),
    // Action Required: entity assignment queue is the source of truth (unassigned, entity-scoped)
    fetchEntityAssignmentQueue({ limit: 100 }),
  ])

  // Action Required combines two sources:
  //  1. The entity assignment queue — unassigned services the entity admin may dispatch
  //     (assign + accept on behalf) with backend-filtered providers.
  //  2. Services on `confirmed` orders whose provider is assigned but has not accepted yet —
  //     these get the Accept/Reject-on-behalf variant. Derived from the company order nodes
  //     already fetched above (no extra request).
  const pendingAcceptance = buildPendingAcceptanceServicesFromOrderNodes(upcomingOrderNodes || [])
  const seenServiceIds = new Set((entityAssignmentQueue || []).map((s) => s.id))
  const actionRequiredServices: ProviderService[] = [
    ...(entityAssignmentQueue || []),
    ...pendingAcceptance.filter((s) => !seenServiceIds.has(s.id)),
  ]

  const currentMonthRevenue = monthlyRevenueResult.amount
  const revenueCurrency = monthlyRevenueResult.currency

  const upcomingServices = buildUpcomingServiceRowsFromOrderNodes(upcomingOrderNodes || [])

  return {
    organizationId: orgId,
    orderStatistics,
    activeProvidersCount,
    currentMonthRevenue,
    revenueCurrency,
    actionRequiredServices,
    upcomingServices,
  }
}

/**
 * Fetch the "Services Completed This Week" chart data on its own, decoupled
 * from the live dashboard bundle so it can be cached with a long TTL.
 *
 * Built from order nodes (so completed orders/services are included) with a
 * fallback to provider services when the order nodes yield nothing.
 */
export async function fetchWeeklyCompletedServices(
  organizationId: string,
): Promise<WeeklyServiceData[]> {
  const orgId = orgUuid(organizationId)

  const [orderNodes, providerServices] = await Promise.all([
    fetchUpcomingOrdersOrderDetailsStyle(orgId),
    fetchProviderServices({ limit: 200 }),
  ])

  const weeklyFromOrders = buildWeeklyCompletedServicesChartFromOrderNodes(orderNodes || [])
  const weeklyFromProviderServices = buildWeeklyCompletedServicesChart(providerServices || [])
  return weeklyFromOrders.some((d) => d.services > 0)
    ? weeklyFromOrders
    : weeklyFromProviderServices
}

/**
 * Fetch the week-over-week performance bundle: this-week vs last-week completed
 * services by day, WoW KPI deltas (completed / orders / revenue / completion rate),
 * the current status pipeline, and top services / categories / providers.
 *
 * Cached independently (long TTL) like `fetchWeeklyCompletedServices`, and its
 * query key nests under `dashboardKeys.all` so service actions still invalidate it.
 */
export async function fetchWeeklyPerformance(
  organizationId: string,
): Promise<WeeklyPerformance> {
  const orgId = orgUuid(organizationId)
  const thisWeek = currentWeekWindow()
  const lastWeek = weekWindowBefore(1)

  // Own fetch with a higher limit so a two-week aggregate has enough history.
  // NOTE: GET_COMPANY_ORDERS_FOR_DASHBOARD has no date filter and no guaranteed
  // ordering, so on very high-volume orgs this two-week aggregate is best-effort
  // and may undercount. The correct fix is date-range args on that query
  // (bundle with the serviceCategory backend change); until then we raise the limit.
  const nodes = (await fetchUpcomingOrdersOrderDetailsStyle(orgId, 500)) || []

  const kpis: WeeklyPerformance['kpis'] = {
    completed: {
      current: countCompletedInWindow(nodes, thisWeek),
      previous: countCompletedInWindow(nodes, lastWeek),
    },
    completionRate: {
      current: completionRateInWindow(nodes, thisWeek),
      previous: completionRateInWindow(nodes, lastWeek),
    },
  }

  return {
    thisWeekByDay: buildWeeklyCompletedServicesChartFromOrderNodes(nodes, thisWeek),
    lastWeekByDay: buildWeeklyCompletedServicesChartFromOrderNodes(nodes, lastWeek),
    kpis,
    statusBreakdown: buildStatusBreakdown(nodes, thisWeek),
    topServices: buildCompletedBreakdown(nodes, thisWeek, (s) => s.serviceName),
    topServiceCategories: buildCompletedBreakdown(nodes, thisWeek, (s) => s.serviceCategory),
    topProviders: buildCompletedBreakdown(nodes, thisWeek, (s) => s.providerName),
  }
}
